import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import { Stamp, FileText, Check, X, Clock, PencilSimple, Trash, Plus, Export, Target, Stack, Sparkle, Trophy } from "@phosphor-icons/react";

const STATUS = {
  Approved: { c: "emerald", I: Check },
  Review:   { c: "amber",   I: Clock },
  Offered:  { c: "indigo",  I: Stamp },
  Waitlist: { c: "rose",    I: X },
};

const FIELDS = [
  { name: "name", label: "Applicant name", type: "text" },
  { name: "program", label: "Program", type: "select", options: [
    { value: "B.Tech CSE", label: "B.Tech CSE" }, { value: "B.Tech ECE", label: "B.Tech ECE" },
    { value: "MBA Core", label: "MBA Core" }, { value: "M.Tech AI", label: "M.Tech AI" },
    { value: "B.Sc Physics", label: "B.Sc Physics" },
  ]},
  { name: "cgpa", label: "CGPA / 12th %", type: "number" },
  { name: "docs", label: "Documents submitted (out of 6)", type: "number" },
  { name: "status", label: "Status", type: "select", options: [
    { value: "Review", label: "Review" }, { value: "Approved", label: "Approved" },
    { value: "Offered", label: "Offered" }, { value: "Waitlist", label: "Waitlist" },
  ]},
];

export default function Admissions() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("pipeline");
  const [apps, setApps] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [rules, setRules] = useState([]);
  const [seats, setSeats] = useState([]);
  const [eligPanel, setEligPanel] = useState({ program: "B.Tech CSE", category: "GEN", marks: 75, age: 18, subjects: "Physics,Maths", result: null });
  const [ruleForm, setRuleForm] = useState({ program: "B.Tech CSE", category: "GEN", min_marks: 75, required_subjects: "" });
  const [seatForm, setSeatForm] = useState({ program: "B.Tech CSE", category: "GEN", intake: 60, confirmed: 0, provisional: 0, waitlisted: 0 });
  const [allotments, setAllotments] = useState([]);
  const [runResult, setRunResult] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    const [{ data: a }, { data: r }, { data: s }] = await Promise.all([
      api.get("/admissions"), api.get("/admissions/rules"), api.get("/admissions/seat-matrix"),
    ]);
    setApps(a); setRules(r); setSeats(s);
  }, []);
  useEffect(() => { load(); }, [load]);

  const checkEligibility = async () => {
    try {
      const { data } = await api.post("/admissions/check-eligibility", {
        program: eligPanel.program, category: eligPanel.category,
        marks: Number(eligPanel.marks), age: Number(eligPanel.age),
        subjects: eligPanel.subjects.split(",").map(s => s.trim()).filter(Boolean),
      });
      setEligPanel(p => ({ ...p, result: data }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const saveRule = async () => {
    try {
      await api.post("/admissions/rules", {
        ...ruleForm, min_marks: Number(ruleForm.min_marks),
        required_subjects: ruleForm.required_subjects.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Rule saved");
      setRuleForm({ program: "B.Tech CSE", category: "GEN", min_marks: 75, required_subjects: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete rule?")) return;
    try { await api.delete(`/admissions/rules/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const saveSeat = async () => {
    try {
      await api.post("/admissions/seat-matrix", {
        ...seatForm,
        intake: Number(seatForm.intake), confirmed: Number(seatForm.confirmed),
        provisional: Number(seatForm.provisional), waitlisted: Number(seatForm.waitlisted),
      });
      toast.success("Seat matrix updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (a) => setModal({ open: true, editing: a });
  const close = () => setModal({ open: false, editing: null });

  const runAllotment = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admissions/allotment/run", { round_no: 1 });
      setRunResult(data);
      toast.success(`Round 1: ${data.allotted_count} allotted, ${data.waitlisted_count} waitlisted`);
      const { data: list } = await api.get("/admissions/allotment");
      setAllotments(list);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };

  const releaseAllotment = async (id) => {
    if (!window.confirm("Release this seat?")) return;
    try { await api.post(`/admissions/allotment/${id}/release`); toast.success("Released"); 
      const { data: list } = await api.get("/admissions/allotment"); setAllotments(list); load();
    } catch { toast.error("Failed"); }
  };

  useEffect(() => { if (tab === "allotment") api.get("/admissions/allotment").then(r => setAllotments(r.data)); }, [tab]);

  const save = async (values) => {
    try {
      if (modal.editing) {
        await api.patch(`/admissions/${modal.editing.app_id}`, values);
        toast.success("Application updated");
      } else {
        await api.post("/admissions", values);
        toast.success("Application created");
      }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete application ${a.app_id} — ${a.name}?`)) return;
    try { await api.delete(`/admissions/${a.app_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const exportCsv = () => {
    const rows = [["App ID", "Name", "Program", "CGPA", "Docs", "Status"],
      ...apps.map(a => [a.app_id, a.name, a.program, a.cgpa, `${a.docs}/6`, a.status])];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `admissions_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click(); URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div>
      <PageHeader
        overline="M02 · Pre-Enrollment"
        title={t("modules.admissions.title")}
        description={t("modules.admissions.subtitle")}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportCsv} className="rounded-md" data-testid="export-applicants-btn"><Export size={14} className="mr-1.5"/> Export CSV</Button>
            <Button onClick={openCreate} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-application-btn">
              <Plus size={14} className="mr-1.5"/> New application
            </Button>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">
        {/* Tabs */}
        <div className="inline-flex border border-border rounded-md overflow-hidden" data-testid="adm-tabs">
          {[
            { v: "pipeline", l: "Pipeline", I: FileText },
            { v: "eligibility", l: "Eligibility rules", I: Target },
            { v: "seats", l: "Seat matrix", I: Stack },
            { v: "allotment", l: "Allotment", I: Trophy },
          ].map(it => (
            <button
              key={it.v}
              onClick={() => setTab(it.v)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 border-r border-border last:border-r-0 ${tab === it.v ? "bg-[hsl(244_53%_20%)] text-white" : "bg-white hover:bg-secondary"}`}
              data-testid={`adm-tab-${it.v}`}
            >
              <it.I size={14}/> {it.l}
            </button>
          ))}
        </div>

        {tab === "pipeline" && (
        <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          {[
            { l: "Applications", v: apps.length, d: "active cycle" },
            { l: "Approved", v: apps.filter(a => a.status === "Approved").length, d: "ready for offer" },
            { l: "Offered", v: apps.filter(a => a.status === "Offered").length, d: "e-signed" },
            { l: "Avg. time to offer", v: "3.8d", d: "AI auto-merit" },
          ].map(s => (
            <div key={s.l} className="bg-white p-5">
              <div className="eos-overline">{s.l}</div>
              <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{s.v}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{s.d}</div>
            </div>
          ))}
        </div>
        </>
        )}

        {tab === "pipeline" && (
        <div className="eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <FileText size={16} weight="duotone"/>
            <div className="font-heading font-semibold text-base">Application pipeline · AY 2026-27</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground">
              <tr>{["App ID", "Applicant", "Program", "CGPA", "Docs", "Status", "Actions"].map(h => (
                <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {apps.map(a => {
                const s = STATUS[a.status] || STATUS.Review;
                return (
                  <tr key={a.app_id} className="border-t border-border hover:bg-secondary/40" data-testid={`app-row-${a.app_id}`}>
                    <td className="px-4 py-3 font-mono-eos text-[12px]">{a.app_id}</td>
                    <td className="px-4 py-3 font-medium">{a.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.program}</td>
                    <td className="px-4 py-3 font-mono-eos">{a.cgpa}</td>
                    <td className="px-4 py-3">{a.docs}/6</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-${s.c}-50 text-${s.c}-700`}>
                        <s.I size={11} weight="bold"/> {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" onClick={() => openEdit(a)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${a.app_id}`}><PencilSimple size={13}/></Button>
                        <Button variant="ghost" onClick={() => remove(a)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${a.app_id}`}><Trash size={13}/></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}

        {tab === "eligibility" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="eligibility-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Real-time eligibility check</div>
              <div className="space-y-2">
                {[
                  ["program", "Program"], ["category", "Category"], ["marks", "Marks %"], ["age", "Age"], ["subjects", "Subjects (comma-separated)"],
                ].map(([k, l]) => (
                  <div key={k}>
                    <label className="eos-overline">{l}</label>
                    <input value={eligPanel[k]} onChange={(e) => setEligPanel(p => ({ ...p, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`elig-${k}`}/>
                  </div>
                ))}
                <Button onClick={checkEligibility} className="w-full mt-2 bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="check-eligibility-btn">Check eligibility</Button>
              </div>
              {eligPanel.result && (
                <div className={`mt-4 p-3 border rounded-md ${eligPanel.result.status === "pass" ? "border-emerald-200 bg-emerald-50" : eligPanel.result.status === "fail" ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`} data-testid="elig-result">
                  <div className="font-bold text-sm uppercase">{eligPanel.result.status === "pass" ? "✓ PASS" : eligPanel.result.status === "fail" ? "✗ FAIL" : "⚠ REVIEW REQUIRED"}</div>
                  <ul className="text-[12px] mt-1 list-disc list-inside">
                    {eligPanel.result.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Configured rules</div>
              <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
                {rules.length === 0 && <div className="text-sm text-muted-foreground italic">No rules yet.</div>}
                {rules.map(r => (
                  <div key={r.rule_id} className="flex items-start justify-between p-2.5 border border-border rounded-md" data-testid={`rule-${r.rule_id}`}>
                    <div className="text-[12px]">
                      <div className="font-medium">{r.program} <span className="font-mono-eos text-muted-foreground">[{r.category}]</span></div>
                      <div className="text-muted-foreground mt-0.5">
                        {r.min_marks && `min ${r.min_marks}%`}
                        {r.required_subjects?.length > 0 && ` · req: ${r.required_subjects.join(", ")}`}
                      </div>
                    </div>
                    <Button variant="ghost" onClick={() => deleteRule(r.rule_id)} className="h-6 w-6 p-0 text-rose-600" data-testid={`del-rule-${r.rule_id}`}><Trash size={12}/></Button>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-3 space-y-2">
                <div className="eos-overline">Add new rule</div>
                {[
                  ["program", "Program"], ["category", "Category"], ["min_marks", "Min marks %"], ["required_subjects", "Required subjects (csv)"],
                ].map(([k, l]) => (
                  <input key={k} value={ruleForm[k]} onChange={(e) => setRuleForm(f => ({ ...f, [k]: e.target.value }))} placeholder={l} className="w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`rule-input-${k}`}/>
                ))}
                <Button onClick={saveRule} className="w-full bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="save-rule-btn">Add rule</Button>
              </div>
            </div>
          </div>
        )}

        {tab === "seats" && (
          <div className="space-y-4" data-testid="seats-pane">
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground">
                  <tr>{["Program", "Category", "Intake", "Confirmed", "Provisional", "Waitlist", "Available"].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {seats.map(s => {
                    const avail = (s.intake || 0) - (s.confirmed || 0) - (s.provisional || 0);
                    return (
                      <tr key={`${s.program}-${s.category}`} className="border-t border-border" data-testid={`seat-row-${s.program}-${s.category}`}>
                        <td className="px-4 py-3 font-medium">{s.program}</td>
                        <td className="px-4 py-3 font-mono-eos">{s.category}</td>
                        <td className="px-4 py-3 font-mono-eos">{s.intake}</td>
                        <td className="px-4 py-3 font-mono-eos text-emerald-700">{s.confirmed}</td>
                        <td className="px-4 py-3 font-mono-eos text-amber-700">{s.provisional}</td>
                        <td className="px-4 py-3 font-mono-eos text-indigo-700">{s.waitlisted}</td>
                        <td className="px-4 py-3"><span className={`font-mono-eos px-2 py-0.5 rounded ${avail > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{avail}</span></td>
                      </tr>
                    );
                  })}
                  {seats.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-sm text-muted-foreground">No seat matrix configured.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Upsert seat matrix</div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                {[
                  ["program", "Program"], ["category", "Category"], ["intake", "Intake"], ["confirmed", "Confirmed"], ["provisional", "Provisional"], ["waitlisted", "Waitlisted"],
                ].map(([k, l]) => (
                  <div key={k}>
                    <label className="eos-overline">{l}</label>
                    <input value={seatForm[k]} onChange={(e) => setSeatForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`seat-input-${k}`}/>
                  </div>
                ))}
              </div>
              <Button onClick={saveSeat} className="mt-3 bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="save-seat-btn">Save</Button>
            </div>
          </div>
        )}

        {tab === "allotment" && (
          <div className="space-y-4" data-testid="allotment-pane">
            <div className="eos-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-heading font-bold text-base">Round-wise allotment engine</div>
                  <div className="text-[11px] text-muted-foreground">Merit-rank applicants → fill seats by counselling choice + category. Waitlisted applicants are listed below.</div>
                </div>
                <Button onClick={runAllotment} disabled={running} className="bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="run-allotment-btn">
                  <Sparkle size={14} weight="fill" className="mr-1.5"/> {running ? "Running…" : "Run round 1"}
                </Button>
              </div>
              {runResult && (
                <div className="grid grid-cols-2 gap-3 text-center" data-testid="run-result">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md"><div className="text-[10px] uppercase text-emerald-700">Allotted</div><div className="font-mono-eos text-2xl font-bold mt-1 text-emerald-700">{runResult.allotted_count}</div></div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md"><div className="text-[10px] uppercase text-amber-700">Waitlisted</div><div className="font-mono-eos text-2xl font-bold mt-1 text-amber-700">{runResult.waitlisted_count}</div></div>
                </div>
              )}
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground"><tr>{["Rank", "Applicant", "Program", "Category", "Status", "Round", ""].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
                <tbody>
                  {allotments.map(a => (
                    <tr key={a.allotment_id} className="border-t border-border" data-testid={`alt-${a.allotment_id}`}>
                      <td className="px-3 py-2 font-mono-eos">#{a.merit_rank}</td>
                      <td className="px-3 py-2">{a.applicant_name || a.applicant_id}</td>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{a.program}</td>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{a.category}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${a.status === "provisional" ? "bg-amber-50 text-amber-700" : a.status === "released" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{a.status}</span></td>
                      <td className="px-3 py-2 font-mono-eos">R{a.round_no}</td>
                      <td className="px-3 py-2">{a.status === "provisional" && <Button variant="ghost" onClick={() => releaseAllotment(a.allotment_id)} className="h-6 px-2 text-[10px] text-rose-600" data-testid={`release-${a.allotment_id}`}>Release</Button>}</td>
                    </tr>
                  ))}
                  {allotments.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-muted-foreground">No allotments yet. Run round 1 to seed.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit application" : "New application"}
        fields={FIELDS}
        initial={modal.editing || { status: "Review", docs: 0, cgpa: 0 }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Create"}
      />
    </div>
  );
}
